import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Client } from '@/domain/schema/v1';
import { useLoad } from '../hooks/useLoad';
import { useService } from '../hooks/useService';

const STATUS: Record<Client['status'], string> = { active: '진행 중', paused: '보류', terminated: '종결' };

/**
 * 내담자 목록 (UX_FLOW §4, 운영 개편 v1): 찾기 · 상태 칩 · 카드(회기 수·진행률·치료 방향) · 등록.
 * 실명은 목록에 표시하지 않는다.
 */
export function ClientsScreen() {
  const svc = useService();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<Client['status']>('active');
  const [code, setCode] = useState('');
  const [alias, setAlias] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data } = useLoad(async () => {
    const [clients, cards] = await Promise.all([svc.listClients(), svc.clientCards()]);
    const briefs = await Promise.all(clients.map((c) => svc.latestBrief(c.id)));
    return clients.map((c, i) => ({ c, brief: briefs[i], card: cards.find((x) => x.clientId === c.id) }));
  }, [svc]);

  const count = (s: Client['status']) => (data ?? []).filter(({ c }) => c.status === s).length;
  const t = q.trim().toLowerCase();
  const filtered = (data ?? []).filter(({ c }) =>
    t ? [c.clientCode, c.alias ?? '', ...(c.tags ?? [])].some((x) => x.toLowerCase().includes(t)) : c.status === status,
  );

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const c = await svc.createClient({ clientCode: code, ...(alias.trim() ? { alias: alias.trim() } : {}) });
      navigate(`/clients/${c.id}/profile`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="stack">
      <div className="page-head">
        <h1 className="page-title">내담자</h1>
      </div>
      <input
        aria-label="내담자 검색"
        placeholder="코드 · 별칭 · 태그로 찾기"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {!t && (
        <div className="row" role="tablist" aria-label="상태">
          {(['active', 'paused', 'terminated'] as const).map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={status === s}
              className={status === s ? 'pill-btn on' : 'pill-btn'}
              onClick={() => setStatus(s)}
            >
              {STATUS[s]} {count(s)}
            </button>
          ))}
        </div>
      )}
      <div className="grid-cards">
        {filtered.map(({ c, brief, card }) => (
          <Link key={c.id} className="card client-card" to={`/clients/${c.id}`}>
            <span className="row" style={{ justifyContent: 'space-between', width: '100%' }}>
              <strong>{c.clientCode}</strong>
              <span className="muted small">
                {c.alias ? `${c.alias} · ` : ''}
                {c.totalSessions}회기 완료
              </span>
            </span>
            {card && (
              <span className="meter" style={{ width: '100%' }}>
                <span style={{ width: `${card.pct}%` }} />
              </span>
            )}
            <span className={card?.missed ? 'small warn' : 'muted small'}>
              {card?.missed ? `놓친 단계 ${card.missed} · ` : ''}
              {card?.treatment ?? STATUS[c.status]}
              {brief ? ` · 다음 ${brief.targetSessionNumber}회기 준비됨` : ''}
            </span>
          </Link>
        ))}
      </div>
      {data && filtered.length === 0 && (
        <p className="muted">{t ? '찾는 내담자가 없어요.' : '이 상태의 내담자가 없어요.'}</p>
      )}

      <details className="card register-card">
        <summary>＋ 내담자 등록</summary>
        <form onSubmit={onCreate} className="stack">
          <label>
            내담자 코드
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="예: C-001 (실명 금지)" />
          </label>
          <label>
            별칭 (선택)
            <input value={alias} onChange={(e) => setAlias(e.target.value)} />
          </label>
          {error && (
            <p role="alert" className="warn">
              {error}
            </p>
          )}
          <button type="submit" className="primary" disabled={!code.trim()}>
            등록
          </button>
        </form>
      </details>
    </section>
  );
}
